import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { LogoMark } from "../components/Logo";
import { Download, LogOut, Save, Users, CircleCheck, Clock4, CircleX } from "lucide-react";

const STATUS_STYLE = {
  paid: "bg-[#D6E0D8] text-[#3f5a4b]",
  pending: "bg-[#F0EDF7] text-[#5b5470]",
  failed: "bg-[#F4E3E0] text-[#8a4f46]",
  amount_mismatch: "bg-[#F4E3E0] text-[#8a4f46]",
};

const SETTING_FIELDS = [
  ["workshop_title", "Workshop title"],
  ["workshop_subtitle", "Subtitle"],
  ["tagline", "Tagline"],
  ["date", "Date"],
  ["time", "Time"],
  ["duration", "Duration"],
  ["price_inr", "Price (INR)"],
  ["workshop_start_iso", "Workshop start (ISO, e.g. 2026-11-01T10:00:00+05:30)"],
  ["facilitator_name", "Facilitator name"],
  ["facilitator_role", "Facilitator role / credentials line"],
  ["facilitator_bio", "Facilitator bio & qualifications"],
  ["contact_email", "Contact email"],
  ["zoom_join_url", "Zoom join URL"],
  ["zoom_meeting_id", "Zoom meeting ID"],
  ["zoom_passcode", "Zoom passcode"],
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("soma_admin_token") || "");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [regs, setRegs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/admin/registrations", authHeaders),
        api.get("/admin/settings", authHeaders),
      ]);
      setRegs(r.data.registrations);
      setSettings(s.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("soma_admin_token");
        setToken("");
      } else {
        toast.error(formatApiError(e, "Could not load admin data."));
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", loginForm);
      localStorage.setItem("soma_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setLoginError(formatApiError(err, "Login failed."));
    }
  };

  const logout = () => {
    localStorage.removeItem("soma_admin_token");
    setToken("");
  };

  const saveSettings = async () => {
    try {
      const payload = { ...settings, price_inr: parseInt(settings.price_inr, 10) || 0 };
      const { data } = await api.put("/admin/settings", payload, authHeaders);
      setSettings(data);
      toast.success("Settings saved. The website and confirmation emails are updated.");
    } catch (e) {
      toast.error(formatApiError(e, "Could not save settings."));
    }
  };

  const exportCsv = () => {
    const header = "Name,Email,Status,Reference,Amount (INR),Consent,Registered At\n";
    const rows = regs
      .map((r) =>
        [r.full_name, r.email, r.payment_status, r.payment_ref || "", (r.amount_paise || 0) / 100, r.consent ? "yes" : "no", r.created_at]
          .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const url = URL.createObjectURL(new Blob([header + rows], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "registrations.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF7F2] px-6" data-testid="admin-login-page">
        <form
          onSubmit={login}
          className="w-full max-w-sm rounded-[26px] border border-[#D9C3A3]/60 bg-white/80 p-10 shadow-xl shadow-[#262320]/5"
        >
          <div className="mb-8 flex flex-col items-center gap-3">
            <LogoMark size={44} />
            <h1 className="font-serif text-2xl font-light text-[#262320]">Facilitator Login</h1>
            <p className="text-xs text-[#6E6862]">Private area — registrations & settings</p>
          </div>
          <input
            type="email"
            required
            placeholder="Email"
            value={loginForm.email}
            onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
            className="mb-4 w-full rounded-xl border border-[#D9C3A3]/70 bg-white px-4 py-3 text-sm outline-none focus:border-[#5E7D6A]"
            data-testid="admin-email-input"
          />
          <input
            type="password"
            required
            placeholder="Password"
            value={loginForm.password}
            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
            className="mb-5 w-full rounded-xl border border-[#D9C3A3]/70 bg-white px-4 py-3 text-sm outline-none focus:border-[#5E7D6A]"
            data-testid="admin-passcode-input"
          />
          {loginError && (
            <p className="mb-4 rounded-xl bg-[#F4E3E0] px-4 py-3 text-xs text-[#262320]" data-testid="admin-login-error">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="w-full rounded-full bg-[#5E7D6A] py-3.5 text-sm font-medium text-[#FAF7F2] hover:bg-[#4d6a59]"
          >
            Sign in
          </button>
          <Link to="/" className="mt-5 block text-center text-xs text-[#6E6862] hover:text-[#5E7D6A]">
            ← Back to the website
          </Link>
        </form>
      </div>
    );
  }

  const filtered = filter === "all" ? regs : regs.filter((r) => r.payment_status === filter);
  const count = (s) => regs.filter((r) => r.payment_status === s).length;

  return (
    <div className="min-h-screen bg-[#FAF7F2] py-10" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoMark size={36} />
            <div>
              <h1 className="font-serif text-2xl font-light text-[#262320]">Registrations</h1>
              <p className="text-xs text-[#6E6862]">SoulSyncHealing — facilitator dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCsv}
              className="inline-flex items-center gap-2 rounded-full border border-[#5E7D6A] px-5 py-2.5 text-xs text-[#5E7D6A] hover:bg-[#5E7D6A] hover:text-white"
              data-testid="export-csv-button"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-[#262320] px-5 py-2.5 text-xs text-[#FAF7F2] hover:bg-[#5E7D6A]"
              data-testid="admin-logout-button"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>

        <div className="mb-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            ["Total", regs.length, Users, "all"],
            ["Paid", count("paid"), CircleCheck, "paid"],
            ["Pending", count("pending"), Clock4, "pending"],
            ["Failed", count("failed"), CircleX, "failed"],
          ].map(([label, value, Icon]) => (
            <div key={label} className="rounded-2xl border border-[#D9C3A3]/50 bg-white/75 p-6">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.15em] text-[#6E6862]">{label}</p>
                <Icon size={16} className="text-[#5E7D6A]" />
              </div>
              <p className="mt-3 font-serif text-3xl text-[#262320]" data-testid={`stat-${label.toLowerCase()}`}>
                {value}
              </p>
            </div>
          ))}
        </div>

        <div className="mb-4 flex items-center gap-4">
          <label className="text-xs uppercase tracking-[0.15em] text-[#6E6862]" htmlFor="status-filter">
            Filter
          </label>
          <select
            id="status-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-full border border-[#D9C3A3]/70 bg-white px-4 py-2 text-xs text-[#262320] outline-none"
            data-testid="admin-status-filter"
          >
            <option value="all">All registrations</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="amount_mismatch">Amount mismatch</option>
          </select>
          {loading && <span className="text-xs text-[#6E6862]">Refreshing...</span>}
        </div>

        <div className="overflow-x-auto rounded-2xl border border-[#D9C3A3]/50 bg-white/80" data-testid="admin-registrations-table">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-[#D9C3A3]/50 text-xs uppercase tracking-[0.12em] text-[#6E6862]">
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Email</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Transaction ref</th>
                <th className="px-6 py-4">Email sent</th>
                <th className="px-6 py-4">Registered</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-[#6E6862]">
                    No registrations yet.
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-[#F0EDF7] last:border-0">
                  <td className="px-6 py-4 font-medium text-[#262320]">{r.full_name}</td>
                  <td className="px-6 py-4 text-[#6E6862]">{r.email}</td>
                  <td className="px-6 py-4">
                    <span className={`rounded-full px-3 py-1 text-xs ${STATUS_STYLE[r.payment_status] || "bg-gray-100"}`}>
                      {r.payment_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-mono text-xs text-[#6E6862]">{r.payment_ref || "—"}</td>
                  <td className="px-6 py-4 text-xs text-[#6E6862]">{r.email_sent ? "Yes" : "No"}</td>
                  <td className="px-6 py-4 text-xs text-[#6E6862]">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {settings && (
          <div className="mt-14 rounded-[26px] border border-[#D9C3A3]/50 bg-white/80 p-8 lg:p-10" data-testid="admin-settings-panel">
            <h2 className="font-serif text-2xl font-light text-[#262320]">Workshop settings</h2>
            <p className="mt-2 text-sm text-[#6E6862]">
              Edit the workshop details and the Zoom link here — the website and confirmation emails update instantly,
              no code changes needed. The Zoom link is only ever emailed after a verified payment.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
              {SETTING_FIELDS.map(([key, label]) => (
                <div key={key} className={key === "facilitator_bio" || key === "tagline" ? "md:col-span-2" : ""}>
                  <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-[#5E7D6A]" htmlFor={`set-${key}`}>
                    {label}
                  </label>
                  {key === "facilitator_bio" ? (
                    <textarea
                      id={`set-${key}`}
                      rows={3}
                      value={settings[key] || ""}
                      onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                      className="w-full rounded-xl border border-[#D9C3A3]/70 bg-white px-4 py-3 text-sm outline-none focus:border-[#5E7D6A]"
                      data-testid={`settings-${key}`}
                    />
                  ) : (
                    <input
                      id={`set-${key}`}
                      type={key === "price_inr" ? "number" : "text"}
                      value={settings[key] ?? ""}
                      onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                      className="w-full rounded-xl border border-[#D9C3A3]/70 bg-white px-4 py-3 text-sm outline-none focus:border-[#5E7D6A]"
                      data-testid={`settings-${key}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={saveSettings}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#5E7D6A] px-8 py-3.5 text-sm font-medium text-[#FAF7F2] hover:bg-[#4d6a59]"
              data-testid="settings-save-button"
            >
              <Save size={15} /> Save settings
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
